/**
 * ==========================================================================
 *  Every human body in the venue, as geometry.
 *
 *  Four poses and no skeleton between them: a sleeper laid out flat, a sitter
 *  folded onto a bench, a sprawler face down on the boards, and a stander on
 *  his feet. They share a palette and a head and nothing else - a pose here is
 *  not a rotation of another pose, it is a different arrangement of the same
 *  parts, and deriving one from another is what turns a man asleep in a
 *  corridor into a man standing to attention.
 *
 *  All of it is pure construction: hand it the asset store and a spec and it
 *  hands back a group. Nothing here touches a scene, the level, or any state
 *  that changes during a run - `World` owns where these bodies go and what
 *  they do afterwards. The pose helpers at the bottom are the same bargain one
 *  frame at a time: they are given the object to turn and the point to turn it
 *  towards, and they read nothing back.
 *
 *  Seeds are indices rather than anything live, so everybody looks slightly
 *  different and looks the same different every run. The seed bases below are
 *  spaced apart on purpose: seating somebody new in the juhlasali must not
 *  change the colour of the shirt a man already asleep in it is wearing.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { SKULL_SCALE, buildHead, limbGeometry, pickHairStyle } from './Figure';
import { createSign } from './Sign';
import type { SceneAssets } from './SceneAssets';
import { PALETTE } from './surfaces';
import { RESTROOMS } from '../config/restrooms';
import { mulberry32 } from '../utils/prng';
import type { SitterCap, SitterSpec, SleeperSpec } from '../world/Level';

export const SKIN_TONES = [0xe3b18c, 0xc98d67, 0xf0c9a8, 0xa9765a, 0xd79a72];
/** One of these is the old `sleeper` palette entry, so the hall still has a red shirt in it. */
export const SHIRTS = [0x3f5a8a, PALETTE.sleeper, 0x3f6b52, 0x8a7a3f, 0x4a4a55, 0x84543a, 0xd8d3c6];
export const TROUSERS = [0x2f3a52, 0x3a3f46, 0x4a4238, 0x5a5f68];
export const HAIR = [0x2b2119, 0x4a3524, 0x6b5233, 0x8d8579, 0x1c1a18];

/**
 * Where `sitterFigure` starts counting. Clear of both the sleepers' indices
 * and the player's, so that seating somebody in the hall cannot change the
 * colour of anybody already lying in it.
 */
const SITTER_SEED = 201;

/**
 * And where `sprawlerFigure` starts. Clear of the other three for the same
 * reason they are clear of each other.
 */
export const SPRAWLER_SEED = 301;

/**
 * And `standerFigure`'s. Index 0 is the cook out at the muurikka, who is drawn
 * from his own pan rather than from `level.standers`; the level's own standing
 * figures start at 1, so putting one in the hall cannot change the cook's face.
 */
export const STANDER_SEED = 401;

/**
 * Where `raiseArms` rolls its own variation from. Clear of the seeds above for
 * the reason they are clear of each other, and its own stream rather than a
 * second draw from the stander's: an arm's height and a man's face have
 * nothing to do with each other, and sharing a seed would quietly tie them
 * together.
 */
export const CHEER_SEED = 501;

/**
 * Boots. Everybody in the venue is barefoot because everybody in the venue is
 * indoors; the one man who is not is stood on a gravel yard at eight in the
 * morning, and he has something on his feet.
 */
export const BOOTS = 0x33302c;

/**
 * The cans on the DJ's head. Matte black plastic and a paler foam pad, which
 * is what every pair anybody has ever brought to a LAN is made of - and the
 * two-tone is the whole of what makes them read as headphones rather than as
 * two dark lumps beside a face.
 */
const HEADPHONE_SHELL = 0x1c1e22;
const HEADPHONE_PAD = 0x4a4d55;

/**
 * Which shoulder the sauna door's greeting comes off, read the way
 * `raiseArms` reads its own: -1 is `kasi-oikea`, +1 is `kasi-vasen`. The
 * angles themselves are not here at all - they are `GREETING` in
 * src/config/gameplay.ts, which is also where the argument for them is.
 */
export const GREETING_SIDE = -1;

/**
 * How much bend is left in the elbow of an arm that is pointing at something.
 * Pose rather than tuning, so it lives here beside the body it belongs to and
 * not in `src/config/` - the same place the cook's reach over his pan is
 * written down. The angle the arm is *held* at is not here at all: it is read
 * off whatever the man is pointing at. See `pointArm`.
 */
export const POINTING_ELBOW = -0.18;
/** The centred doorway pose needs a bent elbow to keep the hand off the jamb. */
export const MAYSA_BLOCKING_ELBOW = -1.35;

/**
 * And what an arm that is not pointing at anything is held at instead: how far
 * above the horizontal it goes, how far out to the side, and how much of the
 * lift is spent varying it between one arm and the next.
 *
 * Pose rather than tuning, like the elbow above, and chosen rather than
 * derived - see `StanderSpec.cheering` for why there is nothing here to derive
 * it from. `CHEER_STAGGER` is the load-bearing one of the three: two arms at
 * the same angle are a gesture, four of them are a salute, and a whole crowd
 * of them is a rally. The lift is the only thing varied, because the height a
 * hand ends up at is the only part of this anybody sees from the yard.
 */
export const CHEER_LIFT = 1.16;
export const CHEER_STAGGER = 0.52;
export const CHEER_SPLAY = 0.2;
/** More bend than a pointing arm has: nobody holds a lock-out for three hours. */
export const CHEER_ELBOW = -0.32;

/** The axis `limbGeometry` runs along, which is what a bone is turned off. */
export const LIMB_AXIS = new THREE.Vector3(0, 0, 1);

/**
 * One sleeping body, feet at the group origin's -Z end and the ground at y=0.
 *
 * Seeded from the index rather than from anything live: everybody in the hall
 * looks slightly different, and looks the same different every run.
 */
export function sleeperFigure(assets: SceneAssets, spec: SleeperSpec, index: number): THREE.Group {
  const rng = mulberry32(0x51eeb0 + index * 977);
  const group = new THREE.Group();

  const length = spec.length;
  const height = spec.height;
  // Shoulders, not the collider. Never wider than the box it lives in.
  const shoulder = Math.min(spec.width * 0.62, 0.5);

  const skinHex = SKIN_TONES[(rng() * SKIN_TONES.length) | 0];
  const skin = assets.figureMaterial(skinHex);
  const shirt = assets.figureMaterial(SHIRTS[(rng() * SHIRTS.length) | 0]);
  const trousers = assets.figureMaterial(TROUSERS[(rng() * TROUSERS.length) | 0]);
  const hairHex = HAIR[(rng() * HAIR.length) | 0];
  const hair = assets.figureMaterial(hairHex);

  /**
   * One body part: `across` the body, `tall` off the floor, `along` its
   * length. A box unless told otherwise. The limbs are cylinders, because a
   * plank with a sleeve on is still a plank; the trunk stays a box, because
   * a cylinder with a shirt on is a sleeping bag.
   */
  const part = (
    material: THREE.Material,
    across: number,
    tall: number,
    along: number,
    x: number,
    y: number,
    z: number,
    geometry: THREE.BufferGeometry = assets.boxGeometry,
  ): THREE.Mesh => {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.scale.set(across, tall, along);
    mesh.position.set(x, y, z);
    group.add(mesh);
    return mesh;
  };
  const round = limbGeometry();

  // Every part lies on the floor: its `y` is half its own thickness. The
  // body is drawn thinner than its collider is tall for the same reason it
  // is drawn narrower - the box is generous so the slalom is fair, and a
  // man 0.4 m thick lying down is a crate.
  const lie = (thickness: number): number => thickness / 2;

  // Chest, waist, hips. Three pieces rather than one, because the taper from
  // shoulders to waist is most of what says "person" at a glance.
  part(shirt, shoulder, height * 0.72, length * 0.24, 0, lie(height * 0.72), length * 0.28);
  part(shirt, shoulder * 0.85, height * 0.64, length * 0.2, 0, lie(height * 0.64), length * 0.07);
  part(trousers, shoulder * 0.9, height * 0.6, length * 0.13, 0, lie(height * 0.6), -length * 0.06);

  // Legs, slightly apart, feet turned out the way they fall.
  for (const side of [-1, 1]) {
    const spread = shoulder * (0.2 + rng() * 0.05);
    part(trousers, shoulder * 0.34, height * 0.52, length * 0.26, side * spread, lie(height * 0.52), -length * 0.22, round);
    part(trousers, shoulder * 0.29, height * 0.44, length * 0.24, side * spread, lie(height * 0.44), -length * 0.4, round);
    part(skin, shoulder * 0.27, height * 0.46, length * 0.08, side * spread, lie(height * 0.46), -length * 0.49);
  }

  // Arms alongside. One is usually flung further out than the other.
  for (const side of [-1, 1]) {
    const out = side * (shoulder * 0.5 + shoulder * (0.14 + rng() * 0.12));
    part(shirt, shoulder * 0.26, height * 0.42, length * 0.21, out, lie(height * 0.42), length * 0.22, round);
    part(skin, shoulder * 0.23, height * 0.36, length * 0.2, out, lie(height * 0.36), length * 0.03, round);
    part(skin, shoulder * 0.22, height * 0.3, length * 0.07, out, lie(height * 0.3), -length * 0.1);
  }

  // Head. Kept small on purpose: a head sized off the *height* of a body this
  // flat comes out enormous, and one big ball undoes every other proportion.
  const radius = Math.min(height * 0.4, shoulder * 0.28);
  const headZ = length * 0.5 - radius * 1.1;
  const turn = (rng() - 0.5) * 1.1;

  part(skin, radius * 0.7, radius * 1.2, length * 0.05, 0, radius * 0.85, headZ - radius * 0.95, round);

  const head = buildHead({
    radius,
    skin: skinHex,
    hair: hairHex,
    style: pickHairStyle(rng),
    expression: 'asleep',
    skinMaterial: skin,
    hairMaterial: hair,
    rng,
    stubble: rng() < 0.5,
    moustache: rng() < 0.12,
  });
  // Built upright and facing +Z, so: laid on its back (x), crown turned to
  // point down the body's +Z end (y), then lolled to one side (z) - and the
  // order is the order those happen in, innermost first.
  head.rotation.set(-Math.PI / 2, Math.PI, turn, 'ZYX');
  head.position.set(0, radius * 0.92, headZ);
  group.add(head);

  return group;
}

/**
 * One man sitting down, with the floor under his hips at the group's origin
 * and local +Z the way he is looking.
 *
 * Built upright rather than by standing a sleeper on end. `sleeperFigure`
 * lays a body out with its legs straight and its arms alongside, and a
 * straight-legged body turned upright is a man at attention rather than a
 * man sat on a bench - the pose is the whole difference between the two, so
 * they share the palette and nothing else.
 *
 * Every proportion is a fraction of his standing height except where the
 * bench gets a say: his feet are on the floor whatever the seat does, so
 * `seatHeight` is what decides how far down the shin has to reach.
 */
export function sitterFigure(assets: SceneAssets, spec: SitterSpec, index: number, passedOut = false): THREE.Group {
  const rng = mulberry32(0x51eeb0 + (SITTER_SEED + index) * 977);
  const group = new THREE.Group();

  const torso = new THREE.Group();
  torso.name = 'ylavartalo';
  group.add(torso);

  const h = spec.height;
  const seat = spec.seatHeight;
  const shoulder = h * 0.26;
  /** Hip to knee: everything a seated man puts in front of his bench. */
  const knee = h * 0.25;
  const ankle = h * 0.03;
  const kneeTop = seat + h * 0.06;

  const skinHex = SKIN_TONES[(rng() * SKIN_TONES.length) | 0];
  const skin = assets.figureMaterial(skinHex);
  const shirt = assets.figureMaterial(SHIRTS[(rng() * SHIRTS.length) | 0]);
  const trousers = assets.figureMaterial(TROUSERS[(rng() * TROUSERS.length) | 0]);
  const hairHex = HAIR[(rng() * HAIR.length) | 0];
  const hair = assets.figureMaterial(hairHex);

  /** One part: `across` him, `tall` off the floor, `deep` the way he faces. */
  const part = (
    parent: THREE.Object3D,
    material: THREE.Material,
    across: number,
    tall: number,
    deep: number,
    x: number,
    y: number,
    z: number,
  ): THREE.Mesh => {
    const mesh = new THREE.Mesh(assets.boxGeometry, material);
    mesh.scale.set(across, tall, deep);
    mesh.position.set(x, y, z);
    parent.add(mesh);
    return mesh;
  };

  // Hips on the bench, then waist and chest. Three boxes for the same reason
  // a sleeper gets three: the taper is most of what says "person".
  part(group, trousers, shoulder * 0.86, h * 0.1, h * 0.17, 0, seat + h * 0.05, 0);
  part(torso, shirt, shoulder * 0.78, h * 0.14, h * 0.13, 0, seat + h * 0.17, -h * 0.01);
  part(torso, shirt, shoulder, h * 0.155, h * 0.145, 0, seat + h * 0.275, -h * 0.015);

  for (const side of [-1, 1]) {
    const x = side * shoulder * 0.24;
    // Thigh out to the knee, shin down to the floor, and the foot just past
    // it. Bare, like every other body in the building: this is a house, and
    // the shoes came off at the door.
    part(group, trousers, shoulder * 0.32, h * 0.075, knee + h * 0.06, x, seat + h * 0.045, knee * 0.42);
    part(group, trousers, shoulder * 0.28, kneeTop - ankle, h * 0.09, x, (ankle + kneeTop) / 2, knee - h * 0.01);
    part(group, skin, shoulder * 0.26, h * 0.04, h * 0.13, x, h * 0.02, knee + h * 0.02);
  }

  for (const side of [-1, 1]) {
    const x = side * shoulder * 0.55;
    // Upper arms hanging, forearms along the thighs, hands on the knees.
    // Which is what a man does with his arms when he is watching a room and
    // is not holding anything.
    part(torso, shirt, shoulder * 0.26, h * 0.19, h * 0.11, x, seat + h * 0.26, -h * 0.01);
    if (passedOut) {
      part(torso, shirt, shoulder * 0.24, h * 0.17, h * 0.1, x, seat + h * 0.08, 0);
      part(torso, skin, shoulder * 0.22, h * 0.06, h * 0.1, x, seat - h * 0.035, 0);
    } else {
      part(torso, shirt, shoulder * 0.24, h * 0.065, h * 0.2, x * 0.95, seat + h * 0.115, knee * 0.4);
      part(torso, skin, shoulder * 0.22, h * 0.05, h * 0.1, x * 0.9, seat + h * 0.1, knee - h * 0.02);
    }
  }

  // The neck, narrower than the head over it: a neck as wide as the skull is a pillar with a face on.
  part(torso, skin, shoulder * 0.22, h * 0.08, h * 0.075, 0, seat + h * 0.36, -h * 0.01);

  // Head, and whatever is on it, in a group of their own: a man looking
  // slightly off to one side takes his cap and his lettering with him.
  const radius = h * 0.065;
  const head = new THREE.Group();
  head.position.set(0, seat + h * 0.455, 0);
  head.rotation.y = (rng() - 0.5) * 0.5;
  torso.add(head);

  const cap = spec.cap;
  head.add(
    buildHead({
      radius,
      skin: skinHex,
      hair: hairHex,
      style: cap ? 'capped' : pickHairStyle(rng),
      expression: passedOut ? 'asleep' : 'awake',
      skinMaterial: skin,
      hairMaterial: hair,
      rng,
      stubble: rng() < 0.5,
    }),
  );
  if (cap) addCap(assets, head, cap, radius, h);

  if (passedOut) {
    // Bend above the hips so he stays seated while his head and arms sag.
    const pivot = seat + h * 0.1;
    for (const child of torso.children) child.position.y -= pivot;
    torso.position.y = pivot;
    torso.rotation.x = RESTROOMS.sleeper.slump;
    head.rotation.x = RESTROOMS.sleeper.headDrop;
    head.rotation.z = RESTROOMS.sleeper.headTilt;
  }

  return group;
}

/**
 * A pair of cans over somebody's ears.
 *
 * The one thing about the man in the tent that has to read from the mouth of
 * the parking lot, which is fifteen metres away and closing at 30 km/h. So
 * they are drawn big - a cup the width of the skull's own ear and standing
 * proud of it - because a faithfully proportioned pair of headphones on a
 * 12 cm head is four dark pixels.
 *
 * Hung on the head group like a cap is, so a man who turns his head takes
 * them with him. Everything is a fraction of the skull for the same reason a
 * cap's is: headphones are fitted to a head, not to a man.
 */
export function addHeadphones(assets: SceneAssets, head: THREE.Object3D, radius: number): void {
  const shell = assets.figureMaterial(HEADPHONE_SHELL);
  const pad = assets.figureMaterial(HEADPHONE_PAD);

  // The band over the crown: a half ring standing on edge, so it is an arc
  // from ear to ear rather than a hoop round the head.
  const band = new THREE.Mesh(
    new THREE.TorusGeometry(radius * 1.02, radius * 0.13, 6, 14, Math.PI),
    shell,
  );
  band.rotation.y = Math.PI / 2;
  band.position.y = radius * 0.06;
  head.add(band);

  for (const side of [-1, 1]) {
    // The cup, lying on its side against the ear, and the earpad inside it a
    // shade proud so the two do not fight for the same pixels.
    const cup = new THREE.Mesh(
      new THREE.CylinderGeometry(radius * 0.52, radius * 0.52, radius * 0.3, 12),
      shell,
    );
    cup.rotation.z = Math.PI / 2;
    cup.position.set(side * radius * 1.02, -radius * 0.02, -radius * 0.06);
    head.add(cup);

    const cushion = new THREE.Mesh(
      new THREE.CylinderGeometry(radius * 0.44, radius * 0.44, radius * 0.16, 12),
      pad,
    );
    cushion.rotation.z = Math.PI / 2;
    cushion.position.set(side * radius * 0.88, -radius * 0.02, -radius * 0.06);
    head.add(cushion);
  }
}

/**
 * The cap on somebody's head, and the word stitched across the front of it.
 *
 * Shared by everybody in the venue wearing one, because a cap is the same
 * thing however the man under it is posed - the sitter in the corner of the
 * juhlasali and the cook out at the muurikka are two bodies and one hat. It
 * goes on a head group built facing +Z, over the `capped` hairline
 * `buildHead` paints when it is told there is one coming.
 *
 * `unit` is the wearer's standing height and it decides exactly one number,
 * the thickness of the peak. Everything else is a fraction of the skull,
 * because a cap is fitted to a head and not to a man.
 */
export function addCap(assets: SceneAssets, head: THREE.Object3D, cap: SitterCap, radius: number, unit: number): void {
  const cloth = assets.figureMaterial(new THREE.Color(cap.background).getHex());
  const part = (across: number, tall: number, deep: number, y: number, z: number): THREE.Mesh => {
    const mesh = new THREE.Mesh(assets.boxGeometry, cloth);
    mesh.scale.set(across, tall, deep);
    mesh.position.set(0, y, z);
    head.add(mesh);
    return mesh;
  };

  // The crown stops above the brow: a cap that came down over the eyes
  // would be a cap over the face, and he is awake to look at the room.
  const crown = new THREE.Mesh(
    new THREE.SphereGeometry(radius * 1.06, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.42),
    cloth,
  );
  crown.scale.copy(SKULL_SCALE);
  crown.position.y = radius * 0.06;
  head.add(crown);

  // The stiff front panel, standing a little proud of the crown, and the
  // peak under it tipped down the way one is that has been worn a while. The
  // panel is there so the lettering has something flat to sit on: a word
  // wrapped round a sphere is not a word from two metres away.
  //
  // The peak is what sets the height of everything above it. It juts out
  // further than the panel does and it is tipped up at the back, so its rear
  // edge stands proud of the cap's own front - and a word written level with
  // that edge is a word written behind it. So the peak is placed first and
  // the panel sits above where it ends: the lettering starts where the peak
  // stops, which is also where it starts on a cap you could pick up.
  const peakY = radius * 0.24;
  const panelY = radius * 0.66;
  const panelZ = radius * 0.98;
  part(radius * 1.6, radius * 0.72, radius * 0.36, panelY, panelZ);
  const peak = part(radius * 1.8, unit * 0.012, radius * 0.8, peakY, radius * 1.4);
  peak.rotation.x = 0.16;

  // And the word itself, drawn by the same thing that draws every other word
  // in the venue. A cap is just a very small sign, hung off a head.
  head.add(
    createSign({
      text: cap.text,
      position: { x: 0, y: panelY, z: panelZ + radius * 0.2 },
      rotationY: 0,
      width: radius * 1.5,
      height: radius * 0.56,
      color: cap.color,
      background: cap.background,
    }),
  );
}
